/**
 * FE-only localStorage for locale + currency preferences (routing, booking hydrate, locale modal).
 * Not a reactive BaseObjectStore — persisted prefs only. Can move this to a more appropriate location in the future.
 */
import { SUPPORTED_CURRENCY_CODES } from '../app/data/static/currency-config.js'
import { SUPPORTED_LOCALES } from '../app/data/static/locale-config.js'

/** localStorage key for persisted locale preference (FE-only) */
export const LOCALE_PREFERENCE_STORAGE_KEY = 'locale_preference'

/** localStorage key for persisted currency preference (FE-only) */
export const CURRENCY_PREFERENCE_STORAGE_KEY = 'currency_preference'

/**
 * @returns {string | null} Saved locale code if present and supported, otherwise null
 */
export function readLocalePreference () {
    try {
        const raw = localStorage.getItem(LOCALE_PREFERENCE_STORAGE_KEY)
        if (!raw || typeof raw !== 'string') {
            return null
        }
        const trimmed = raw.trim()
        return SUPPORTED_LOCALES.has(trimmed) ? trimmed : null
    } catch {
        return null
    }
}

/**
 * Persist locale preference. No-op if code is not supported.
 * @param {string} code
 */
export function writeLocalePreference (code) {
    if (!code || typeof code !== 'string' || !SUPPORTED_LOCALES.has(code.trim())) {
        return
    }
    try {
        localStorage.setItem(LOCALE_PREFERENCE_STORAGE_KEY, code.trim())
    } catch {
        // ignore errors caused by localStorage quota exceeded / incognito mode
        console.warn('Error writing locale preference to localStorage', code)
    }
}

/**
 * @returns {string | null} Saved ISO 4217 code if present and product-supported, otherwise null
 */
export function readCurrencyPreference () {
    try {
        const raw = localStorage.getItem(CURRENCY_PREFERENCE_STORAGE_KEY)
        if (!raw || typeof raw !== 'string') {
            return null
        }
        const trimmed = raw.trim().toUpperCase()
        return SUPPORTED_CURRENCY_CODES.has(trimmed) ? trimmed : null
    } catch {
        return null
    }
}

/**
 * Persist currency preference. No-op if code is not product-supported.
 * @param {string} code
 */
export function writeCurrencyPreference (code) {
    if (!code || typeof code !== 'string') {
        return
    }
    const normalized = code.trim().toUpperCase()
    if (!SUPPORTED_CURRENCY_CODES.has(normalized)) {
        return
    }
    try {
        localStorage.setItem(CURRENCY_PREFERENCE_STORAGE_KEY, normalized)
    } catch {
        // ignore errors caused by localStorage quota exceeded / incognito mode
        console.warn('Error writing currency preference to localStorage', code)
    }
}
